import { useRef, useLayoutEffect, useEffect } from 'react';
import translator from '@Components/Utilities/Translator';

const MediaControl = (props) => {

    const {
        Media = [],
        ShowThumbnails = true,
        ShowMediaOverlayBanner = props.ShowMediaOverlayBanner ?? false,
        MediaOverlayBanner = props.MediaOverlayBanner ?? '',
        allText = 'All',
        threeSixtyText = '360&deg; Photos',
        photosText = 'Photos',
        videosText = 'Videos'
    } = props;

    const mediaType = {
        image: 'Image',
        video: 'Video',
        spin360: 'Spin360'
    };

    const mediaRef = useRef(null);
    const photoThumbnails = Media.filter(item => item.MediaTypeName == mediaType.image && !item.Is360);
    const videoThumbnails = Media.filter(item => item.MediaTypeName == mediaType.video);
    const threeSixtyThumbnails = Media.filter(item => item.Is360 || item.IsSpin360);

    useLayoutEffect(() => {
        // This is in place to close the media control if/when this component gets unmounted from the DOM:
        return () => {
            if (mediaRef.current?.mediaController?._windowState !== 0) {
                mediaRef.current?.mediaController?.$btnZoomExit?.click?.();
            }
        }
    }, []);

    useEffect(() => {
        Media.forEach(item => {
            if (item.Preload) {
                if (item.MediaTypeName == mediaType.image) {
                    new Image().src = item.Is360 ? item.ThumbnailUrl : item.MediaUrl
                } else if (item.MediaTypeName == mediaType.video) {
                    new Image().src = item.PreviewImageUrl;
                }
            }
        });
    }, []);

    const buildMediaItem = (item, index) => {
        switch (item.MediaTypeName) {
            case mediaType.image:
                return (<div key={buildReactKey(item, index, false)} className={'mc-item mc-img' + (index === 0 ? ' mc-selected' : ' mc-item-loading') + (item.Is360 ? ' mc-360' : '')}>
                    {item.Is360 ?
                        <div className='mc-360viewer' data-url360={item.MediaUrl} data-preview360={item.ThumbnailUrl}></div>
                        :
                        <>
                            {index === 0 ?
                                <img src={item.MediaUrl} alt={item.AltText} tabIndex='0' className='mc-img-normal' />
                                :
                                <img data-src={item.MediaUrl} alt={item.AltText} tabIndex='0' className='mc-item-loading mc-img-normal' />}
                            <img data-fullscreen={item.FullScreenUrl} alt={item.AltText} tabIndex='0' className='mc-item-loading mc-img-full' />
                        </>}
                </div>);
                break;
            case mediaType.video:
                return (<div key={buildReactKey(item, index, false)} className={'mc-item mc-video' + (index === 0 ? ' mc-selected' : ' mc-item-loading') + (item.Is360 ? ' mc-360 mc-video360' : '') + (item.IsExternal ? ' mc-external' : '')}>
                    {item.TranscriptURL &&
                        <a href={item.TranscriptURL} className='transcript-link hidden' target='_blank' ><span className='hidden'>{translator.getPlainTextPhrase('sandhills.downloadtranscript')}</span></a>}
                    <div>
                        <video className='videoElement video-js vjs-default-skin vjs-big-play-centered' tabIndex='0' playsinline controls preload='none' poster={item.PreviewImageUrl}>
                            {item.MediaSources.map((source) =>
                                <source key={'mc-vid-src' + source.Label} src={source.WebUrl} type={source.MIMEType} label={source.Label} res={source.VerticalResolution} />)}
                        </video>
                    </div>
                </div>);
                break;
            case mediaType.spin360:
                return (<div key={buildReactKey(item, index, false)} className={'mc-item mc-spin360' + (index === 0 ? ' mc-selected' : ' mc-item-loading')}>
                    <div class='cloudimage-360' id={item.ID} data-image-list-x={item.MediaUrl} data-keys='true'></div>
                </div>);
                break;
        }
    };

    const buildReactKey = (item, index, isThumbnail) => {
        return 'mc-' + (isThumbnail ? 'thumbnail-' : '') + item.MediaTypeName + '-' + index + (item.Is360 ? '-Is360' : '');
    };

    return (
        <div ref={mediaRef} className='media-control mc-normal'>
            <pre className='mc-debuginfo' />
            <div className='media-overlay' />
            <div className='mc-media-viewport media-4-3'>
                <div className='mc-nav-controls'>
                    <div className='mc-btn mc-zoom-exit' />
                    <div className='mc-btn mc-zoom-open'>
                        <span className='mc-icon-fullscreen-enter' />
                    </div>
                    {(Media.length > 1) &&
                        <>
                            <div className='mc-btn mc-icon-navarrow mc-btn-prev'>
                                <span className='mc-icon-angle-left' />
                            </div>
                            <div className='mc-btn mc-icon-navarrow mc-btn-next'>
                                <span className='mc-icon-angle-right' />
                            </div>
                        </>}
                </div>
                <div className='mc-items'>
                    {ShowMediaOverlayBanner &&
                        <span className='mc-media-overlay-banner-span'>
                            <strong className={'mc-media-overlay-banner'}>{MediaOverlayBanner}</strong>
                        </span>}
                    {(Media.length > 0) && Media.map((item, index) => buildMediaItem(item, index))}
                </div>
                <noscript />
            </div>
            {(ShowThumbnails && Media.length > 0) &&
                <div className='mc-thumbs'>
                    <div className='mc-thumb-toggles-container'>
                        <div className='mc-thumb-toggles mc-inline-flex-center'>
                            {((photoThumbnails.length > 0 && videoThumbnails.length > 0) || (photoThumbnails.length > 0 && threeSixtyThumbnails.length > 0) || (videoThumbnails.length > 0 && threeSixtyThumbnails.length > 0)) &&
                                <div className='mc-btn mc-toggle-all mc-inline-flex-center' tabIndex='0' role='button'>
                                    <span><span dangerouslySetInnerHTML={{ __html: allText }} /></span><span>({Media.length})</span>
                                </div>}
                            {(threeSixtyThumbnails.length > 0) &&
                                <div className='mc-btn mc-toggle-360 mc-inline-flex-center mc-icon-360' tabIndex='0' role='button'>
                                    <span><span dangerouslySetInnerHTML={{ __html: threeSixtyText }} /></span><span>({threeSixtyThumbnails.length})</span>
                                </div>}
                            {(photoThumbnails.length > 0) &&
                                <div className='mc-btn mc-toggle-photos mc-inline-flex-center mc-icon-image' tabIndex='0' role='button'>
                                    <span><span dangerouslySetInnerHTML={{ __html: photosText }} /></span><span>({photoThumbnails.length})</span>
                                </div>}
                            {(videoThumbnails.length > 0) &&
                                <div className='mc-btn mc-toggle-videos mc-inline-flex-center mc-icon-video' tabIndex='0' role='button'>
                                    <span><span dangerouslySetInnerHTML={{ __html: videosText }} /></span><span>({videoThumbnails.length})</span>
                                </div>}
                        </div>
                    </div>
                    <div className='mc-thumb-strip'>
                        <div className='mc-btn mc-btn-prev'>
                            <span className='mc-icon-angle-left' />
                        </div>
                        <div className='mc-btn mc-btn-next'>
                            <span className='mc-icon-angle-right' />
                        </div>
                        <div className='mc-thumb-slider'>
                            <ul>
                                {Media.map((item, index) =>
                                    <li key={buildReactKey(item, index, true)} data-type={item.Is360 || item.IsSpin360 ? '360' : item.MediaTypeName} className={'mc-li-' + item.MediaTypeName.toLowerCase()}>
                                        <div className='mc-item mc-thumb mc-item-loading'>
                                            <img data-src={item.ThumbnailUrl} className='mc-item-loading' tabindex='0' alt={''} style={{ display: 'none' }} />
                                        </div>
                                    </li>)}
                            </ul>
                        </div>
                    </div>
                </div>}
        </div>
    );
};

export default MediaControl;